import React, { useState } from 'react'
import { Collapse } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import { Link } from 'react-router-dom'
import { CN_DOCUMENT_COPY } from 'src/utils/creditNoteDocumentHelpers'
import { introCollapsedByDefault } from 'src/utils/introCollapsedByDefault'
import { stripTransientProps } from 'src/utils/stripTransientProps'

const { Panel } = Collapse

// styled-components 4 forwards every prop through `as` to a component.
const StepLink = stripTransientProps(Link)

const TAB_COPY = {
  orders: {
    lead: 'This is what you ordered — the first step of a three-way match. When an order didn’t go through OMS, add it here as a manual order so you can still receive and reconcile it.',
    support:
      'Next: confirm what arrived on Goods receipting, then match the supplier invoice on Invoices & Credit Notes.',
  },
  receipting: {
    lead: 'Confirm what actually arrived — the second step of a three-way match. Fix shortages, damage, or wrong items here so you get your money back, then Full accept when the delivery is right.',
    support:
      'Doing this keeps Spend over time, Gross Profit, and accounting exports accurate. If the order isn’t in OMS yet, add it on Orders first. When the invoice arrives, upload or match it on Invoices & Credit Notes.',
  },
  invoices: {
    lead: 'Match supplier invoices to the orders you placed in OMS — the third step of a three-way match. Upload invoices yourself anytime with Add Invoices / Credit Notes — don’t wait on suppliers. Many smaller suppliers won’t email or EDI, and uploading yourself is the reliable path.',
    support:
      'You can also ask suppliers to email invoices@ordersmadesimple.com, use the free portal, or connect via free EDI — with EDI, perfect matches can auto-reconcile within your match tolerance. Either way, check this tab regularly: not every invoice lines up perfectly, and accurate OMS data needs that occasional match. Delivery wrong? Fix it on Goods receipting. Order missing? Add it on Orders first.',
  },
}

const FLOW_STEPS = [
  {
    key: 'orders',
    label: 'Orders',
    short: 'What you ordered',
    detail:
      'Capture every purchase — including manual orders that didn’t go through OMS — so there is something to receive and match against.',
  },
  {
    key: 'receipting',
    label: 'Goods receipting',
    short: 'What arrived',
    detail:
      'Check deliveries, report issues (broken bottles, mouldy produce, shortages), and Full accept when the delivery is correct. This is where you protect your money.',
  },
  {
    key: 'invoices',
    label: 'Invoices',
    short: 'What you were billed',
    detail:
      'Upload invoices yourself, or let suppliers send them by email / free EDI. Attach and reconcile here. Perfect match → Match & reconcile (EDI can auto-reconcile within tolerance). Mismatch → finish on Goods receipting. Missing order → add it on Orders first.',
  },
]

/**
 * Shared education for the purchasing 3-way match loop.
 * Tab-specific lead copy + expandable guide.
 */
export default function ThreeWayMatchEducation({
  tab = 'invoices',
  venueId,
  defaultExpanded = false,
  children = null,
  style,
}) {
  const copy = TAB_COPY[tab] || TAB_COPY.invoices
  const [userCollapsed, setUserCollapsed] = useState(null)
  const open = !introCollapsedByDefault({
    userChoice: userCollapsed,
    hasData: !defaultExpanded,
  })

  const linkFor = key => {
    if (!venueId) return null
    if (key === 'orders') return `/v/${venueId}/purchasing/orders`
    if (key === 'receipting') return `/v/${venueId}/purchasing/receipting`
    return `/v/${venueId}/purchasing/invoices`
  }

  return (
    <IntroCard style={style}>
      <LeadBlock>
        <LeadEyebrow>Three-way match</LeadEyebrow>
        <LeadText>{copy.lead}</LeadText>
        <SupportText>{copy.support}</SupportText>
      </LeadBlock>

      <FlowRow role='list' aria-label='Order, receive, match invoice'>
        {FLOW_STEPS.map((step, index) => {
          const active = step.key === tab
          const href = linkFor(step.key)
          const content = (
            <>
              <StepIndex $active={active}>{index + 1}</StepIndex>
              <StepCopy>
                <StepLabel $active={active}>{step.label}</StepLabel>
                <StepShort>{step.short}</StepShort>
              </StepCopy>
            </>
          )

          return (
            <StepItem key={step.key} role='listitem'>
              {href && !active ? (
                <StepSurface as={StepLink} to={href}>
                  {content}
                </StepSurface>
              ) : (
                <StepSurface $active={active}>{content}</StepSurface>
              )}
              {index < FLOW_STEPS.length - 1 ? (
                <StepConnector aria-hidden />
              ) : null}
            </StepItem>
          )
        })}
      </FlowRow>

      <GuideCollapse
        bordered={false}
        activeKey={open ? ['guide'] : []}
        onChange={keys => setUserCollapsed(!keys.includes('guide'))}
      >
        <Panel
          key='guide'
          header={
            <GuideHeader>
              <InfoCircleOutlined />
              <span>How the 3-way match works</span>
            </GuideHeader>
          }
        >
          <GuideBody>
            <GuideIntro>
              When the order, the delivery, and the invoice all agree, you know
              everything is fine. If they don’t, fix the weak link — don’t force
              a match.
            </GuideIntro>
            <GuideSteps>
              {FLOW_STEPS.map((step, index) => (
                <GuideStep key={step.key} $active={step.key === tab}>
                  <GuideStepTitle>
                    <span>{index + 1}.</span> {step.label}
                  </GuideStepTitle>
                  <GuideStepDetail>{step.detail}</GuideStepDetail>
                </GuideStep>
              ))}
            </GuideSteps>
            <GuideFoot>
              Completing the three-way match keeps Spend over time, Gross
              Profit, and Xero / Sage exports accurate — skipped steps leave
              gaps in your numbers. Prefer to stay in control? Upload every
              invoice yourself. Or ask suppliers to send to{' '}
              <strong>invoices@ordersmadesimple.com</strong>, via the free
              portal, or free EDI — OMS reads them and suggests matches (EDI
              perfect matches can auto-reconcile within tolerance). You’ll still
              want Invoices now and then for anything that doesn’t line up.{' '}
              Invoice with no OMS order? Fix delivery on Goods receipting, or
              add a manual order on Orders if it was placed outside OMS — then
              return here to Match.
            </GuideFoot>
            {tab === 'invoices' ? (
              <CreditNoteHelp data-testid='credit-note-3way-help'>
                <CreditNoteHelpTitle>
                  {CN_DOCUMENT_COPY.coachModalTitle}
                </CreditNoteHelpTitle>
                <CreditNoteHelpBody>
                  {CN_DOCUMENT_COPY.coachModalBody}
                </CreditNoteHelpBody>
              </CreditNoteHelp>
            ) : null}
            {children}
          </GuideBody>
        </Panel>
      </GuideCollapse>
    </IntroCard>
  )
}

const IntroCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: left;
`

const LeadBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`

const LeadEyebrow = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7a9a14;
`

const LeadText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.85);
`

const SupportText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #595959;
`

const FlowRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px;
  width: 100%;
`

const StepItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 140px;
`

const StepSurface = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${props => (props.$active ? '#f6ffed' : '#fafafa')};
  border: 1px solid
    ${props =>
      props.$active ? 'rgba(181, 217, 34, 0.65)' : 'rgba(0, 0, 0, 0.06)'};
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover,
  &:focus {
    background: #f6ffed;
    border-color: rgba(181, 217, 34, 0.65);
    color: inherit;
  }
`

const StepIndex = styled.span`
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  color: ${props => (props.$active ? '#ffffff' : '#7a9a14')};
  background: ${props =>
    props.$active ? '#b5d922' : 'rgba(181, 217, 34, 0.25)'};
`

const StepCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`

const StepLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${props => (props.$active ? '#135200' : 'rgba(0, 0, 0, 0.85)')};
  line-height: 1.25;
`

const StepShort = styled.span`
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.3;
`

const StepConnector = styled.span`
  display: none;

  @media (min-width: 900px) {
    display: block;
    position: absolute;
    right: -6px;
    top: 50%;
    width: 10px;
    height: 1px;
    background: rgba(0, 0, 0, 0.12);
    transform: translateY(-50%);
    z-index: 1;
  }
`

const GuideCollapse = styled(Collapse)`
  && {
    background: transparent;
    border: none;

    .ant-collapse-item {
      border-bottom: none;
    }

    .ant-collapse-header {
      align-items: center !important;
      padding: 4px 0 0 28px !important;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.65);
    }

    .ant-collapse-arrow {
      left: 0 !important;
    }

    .ant-collapse-content-box {
      padding: 12px 0 0 !important;
    }
  }
`

const GuideHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`

const GuideBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const GuideIntro = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #595959;
`

const GuideSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const GuideStep = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: ${props => (props.$active ? '#f6ffed' : '#fafafa')};
  border: 1px solid
    ${props =>
      props.$active ? 'rgba(181, 217, 34, 0.55)' : 'rgba(0, 0, 0, 0.06)'};
`

const GuideStepTitle = styled.div`
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);

  span {
    color: #7a9a14;
  }
`

const GuideStepDetail = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #595959;
`

const GuideFoot = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #8c8c8c;

  strong {
    color: rgba(0, 0, 0, 0.65);
    font-weight: 600;
  }
`

const CreditNoteHelp = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: #f6ffed;
  border: 1px solid rgba(181, 217, 34, 0.45);
`

const CreditNoteHelpTitle = styled.p`
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: #135200;
`

const CreditNoteHelpBody = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #3d5c00;
`
